// ContactsManager.jsx
import React, { useState, useEffect } from 'react';
import { contactsAPI } from '../api';
import { toast } from 'react-toastify';
import './ContactsManager.css';

const ContactsManager = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: '', status: '' });
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateData, setUpdateData] = useState({ status: '', adminNotes: '', assignedTo: '' });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [filter]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.type) params.type = filter.type;
      if (filter.status) params.status = filter.status;
      
      const response = await contactsAPI.getAll(params);
      setContacts(response.data || response);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await contactsAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleUpdateStatus = async (id, status, adminNotes = '', assignedTo = '') => {
    try {
      await contactsAPI.updateStatus(id, { status, adminNotes, assignedTo });
      toast.success('Contact updated successfully');
      fetchContacts();
      fetchStats();
      setShowUpdateModal(false);
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact inquiry?')) {
      try {
        await contactsAPI.delete(id);
        toast.success('Contact deleted successfully');
        fetchContacts();
        fetchStats();
      } catch (error) {
        console.error('Error deleting contact:', error);
        toast.error('Failed to delete contact');
      }
    }
  };

  const viewDetails = (contact) => {
    setSelectedContact(contact);
    setShowDetailsModal(true);
  };

  const openUpdateModal = (contact) => {
    setSelectedContact(contact);
    setUpdateData({
      status: contact.status,
      adminNotes: contact.adminNotes || '',
      assignedTo: contact.assignedTo || ''
    });
    setShowUpdateModal(true);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#ffc107';
      case 'contacted': return '#17a2b8';
      case 'confirmed': return '#28a745';
      case 'completed': return '#20c997';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'virtual_tour': return '🎥';
      case 'schedule_visit': return '🏢';
      case 'general': return '📧';
      default: return '📝';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'virtual_tour': return 'Virtual Tour';
      case 'schedule_visit': return 'Campus Visit';
      case 'general': return 'General Inquiry';
      default: return type;
    }
  };

  return (
    <div className="contacts-manager">
      <div className="page-header">
        <h1>Contact Inquiries</h1>
        <p>Manage all contact form submissions and booking requests</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="contacts-stats">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <h3>Total Inquiries</h3>
              <div className="stat-number">{stats.totalInquiries || 0}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🆕</div>
            <div className="stat-info">
              <h3>New</h3>
              <div className="stat-number">{stats.newInquiries || 0}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-info">
              <h3>Pending</h3>
              <div className="stat-number">{stats.pendingBookings || 0}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <h3>Completed</h3>
              <div className="stat-number">{stats.completedVisits || 0}</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Inquiry Type:</label>
          <select 
            value={filter.type} 
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          >
            <option value="">All Types</option>
            <option value="general">General Inquiry</option>
            <option value="virtual_tour">Virtual Tour</option>
            <option value="schedule_visit">Campus Visit</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Status:</label>
          <select 
            value={filter.status} 
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="contacted">Contacted</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        
        <button 
          className="btn-clear-filters" 
          onClick={() => setFilter({ type: '', status: '' })}
        >
          Clear Filters
        </button>
        
        <button 
          className="btn-export" 
          onClick={() => contactsAPI.exportToCSV(filter)}
        >
          📥 Export to CSV
        </button>
      </div>

      {/* Contacts Table */}
      <div className="contacts-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading contacts...</p>
          </div>
        ) : contacts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No contacts found</h3>
            <p>No contact inquiries match your filters</p>
          </div>
        ) : (
          <table className="contacts-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact Info</th>
                <th>Type</th>
                <th>Course</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact._id}>
                  <td className="contact-name">
                    <div className="name-cell">
                      <div className="avatar">
                        {contact.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="full-name">{contact.name}</div>
                        <div className="contact-phone">{contact.phone}</div>
                      </div>
                    </div>
                  </td>
                  <td className="contact-email">
                    <a href={`mailto:${contact.email}`}>{contact.email}</a>
                  </td>
                  <td>
                    <span className="type-badge">
                      {getTypeIcon(contact.inquiryType)} {getTypeLabel(contact.inquiryType)}
                    </span>
                  </td>
                  <td>{contact.course || 'Not specified'}</td>
                  <td>
                    <span 
                      className="status-badge" 
                      style={{ backgroundColor: getStatusColor(contact.status) }}
                    >
                      {contact.status}
                    </span>
                  </td>
                  <td>
                    {new Date(contact.createdAt).toLocaleDateString()}
                    <br />
                    <small>{new Date(contact.createdAt).toLocaleTimeString()}</small>
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="btn-view" 
                      onClick={() => viewDetails(contact)}
                      title="View Details"
                    >
                      👁️
                    </button>
                    <button 
                      className="btn-edit" 
                      onClick={() => openUpdateModal(contact)}
                      title="Update Status"
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn-delete" 
                      onClick={() => handleDelete(contact._id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedContact && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Contact Details</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="details-section">
                <h3>Personal Information</h3>
                <div className="details-grid">
                  <div><strong>Name:</strong> {selectedContact.name}</div>
                  <div><strong>Email:</strong> <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a></div>
                  <div><strong>Phone:</strong> {selectedContact.phone}</div>
                  <div><strong>Course:</strong> {selectedContact.course || 'Not specified'}</div>
                </div>
              </div>
              
              <div className="details-section">
                <h3>Inquiry Details</h3>
                <div className="details-grid">
                  <div><strong>Type:</strong> {getTypeLabel(selectedContact.inquiryType)}</div>
                  <div><strong>Status:</strong> <span className="status-badge" style={{ backgroundColor: getStatusColor(selectedContact.status) }}>{selectedContact.status}</span></div>
                  <div><strong>Date:</strong> {new Date(selectedContact.createdAt).toLocaleString()}</div>
                  {selectedContact.preferredDate && (
                    <div><strong>Preferred Date:</strong> {new Date(selectedContact.preferredDate).toLocaleDateString()}</div>
                  )}
                  {selectedContact.timeSlot && (
                    <div><strong>Time Slot:</strong> {selectedContact.timeSlot}</div>
                  )}
                  {selectedContact.numberOfAttendees && (
                    <div><strong>Attendees:</strong> {selectedContact.numberOfAttendees}</div>
                  )}
                </div>
              </div>
              
              {selectedContact.message && (
                <div className="details-section">
                  <h3>Message</h3>
                  <p className="message-content">{selectedContact.message}</p>
                </div>
              )}
              
              {selectedContact.specialRequirements && (
                <div className="details-section">
                  <h3>Special Requirements</h3>
                  <p className="message-content">{selectedContact.specialRequirements}</p>
                </div>
              )}
              
              {selectedContact.adminNotes && (
                <div className="details-section">
                  <h3>Admin Notes</h3>
                  <p className="admin-notes">{selectedContact.adminNotes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Update Modal */}
      {showUpdateModal && selectedContact && (
        <div className="modal-overlay" onClick={() => setShowUpdateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Update Contact Status</h2>
              <button className="modal-close" onClick={() => setShowUpdateModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Status:</label>
                <select 
                  value={updateData.status} 
                  onChange={(e) => setUpdateData({ ...updateData, status: e.target.value })}
                >
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Assign To:</label>
                <input 
                  type="text" 
                  value={updateData.assignedTo} 
                  onChange={(e) => setUpdateData({ ...updateData, assignedTo: e.target.value })}
                  placeholder="Staff name"
                />
              </div>
              
              <div className="form-group">
                <label>Admin Notes:</label>
                <textarea 
                  value={updateData.adminNotes} 
                  onChange={(e) => setUpdateData({ ...updateData, adminNotes: e.target.value })}
                  rows="4"
                  placeholder="Add notes about this inquiry..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowUpdateModal(false)}>Cancel</button>
              <button 
                className="btn-save" 
                onClick={() => handleUpdateStatus(
                  selectedContact._id, 
                  updateData.status, 
                  updateData.adminNotes, 
                  updateData.assignedTo
                )}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsManager;